import { Button } from "@heroui/react";
import { Segment } from "@heroui-pro/react";
import { useCallback, useMemo, useState } from "react";

import { AssociateProductModal } from "../associate/modal";
import { DisassociateProductModal } from "../disassociate/modal";
import { ImportScrapedProductModal } from "../import/view";

import { AsyncTagFilter, TagFilterItem } from "./async-tag-filter";
import {
  ActivityCell,
  LinkCell,
  PriceCell,
  ProductCell,
  SourceCell,
  WhitelistCell,
  blacklistedRelatives,
  isPrimaryLink,
} from "./cells";
import { RowActions } from "./row-actions";
import { ImageGalleryModal } from "./image-gallery-modal";

import {
  AppliedFilters,
  BulkBar,
  DataTable,
  FilterSegment,
  FilterSelect,
  FiltersPopover,
  SearchBox,
  notify,
  type AppliedFilter,
  type DataGridColumn,
} from "@/components/kit";
import { useUpdateScrapedProduct } from "@/hooks/api/scraped-product/useUpdateScrapedProduct";
import { useGetAllSuppliers } from "@/hooks/api/supplier";
import { ProductRepository } from "@/repositories/product";
import { ScrapedCategoryRepository } from "@/repositories/scrape-category";
import { ScrapedBrandRepository } from "@/repositories/scraped-brand";
import { ScrapedProduct } from "@/types/scraped-product";

export interface ScrapedProductTableProps {
  items: ScrapedProduct[];
  page: number;
  totalProducts: number;
  totalPage: number;
  changePage: (page: number) => void;
  isLoading: boolean;
  searchTerm: string;
  handleSearchTermChange: (searchTerm: string) => void;
  supplierId: string;
  handleSupplierIdChange: (supplierId: string) => void;
  limit: number;
  handleLimitChange: (limit: number) => void;
  brandFilter: string;
  handleBrandFilterChange: (value: string) => void;
  categoryFilter: string;
  handleCategoryFilterChange: (value: string) => void;
  stockFilter: string;
  handleStockFilterChange: (value: string) => void;
  blacklistFilter: string;
  handleBlacklistFilterChange: (value: string) => void;
  linkedProductStatus: string;
  handleLinkedProductStatusChange: (value: string) => void;
  scrapedCategoryIds: string[];
  handleScrapedCategoryIdsChange: (
    ids: string[],
    items: { id: string; name: string }[],
  ) => void;
  scrapedCategoryInitialItems?: { id: string; name: string }[];
  scrapedBrandIds: string[];
  handleScrapedBrandIdsChange: (
    ids: string[],
    items: { id: string; name: string }[],
  ) => void;
  scrapedBrandInitialItems?: { id: string; name: string }[];
  linkedProductIds: string[];
  handleLinkedProductIdsChange: (
    ids: string[],
    items: { id: string; name: string }[],
  ) => void;
  linkedProductInitialItems?: { id: string; name: string }[];
  selectedProductIds?: Set<string>;
  selectedProductsMap?: Map<string, ScrapedProduct>;
  onSelectionChange?: (
    keys: "all" | Set<string | number>,
    currentItems: ScrapedProduct[],
  ) => void;
  onBulkAssociate?: () => void;
  onBulkDisassociate?: () => void;
}

const STOCK_OPTIONS = [
  { id: "", label: "Any" },
  { id: "in-stock", label: "In stock" },
  { id: "out-of-stock", label: "Out of stock" },
];

const BLACKLIST_OPTIONS = [
  { id: "", label: "Any" },
  { id: "whitelisted", label: "Whitelisted" },
  { id: "blacklisted", label: "Blacklisted" },
];

const HAS_BRAND_OPTIONS = [
  { id: "", label: "Any" },
  { id: "with-brand", label: "Yes" },
  { id: "without-brand", label: "No" },
];

const HAS_CATEGORY_OPTIONS = [
  { id: "", label: "Any" },
  { id: "with-category", label: "Yes" },
  { id: "without-category", label: "No" },
];

const LINK_TABS = [
  { id: "all", label: "All" },
  { id: "linked", label: "Linked" },
  { id: "not-linked", label: "Not linked" },
];

const labelOf = (options: { id: string; label: string }[], id: string) =>
  options.find((option) => option.id === id)?.label ?? id;

/** The filters in the popover are edited as a draft and applied together. */
interface FilterDraft {
  supplierId: string;
  categories: TagFilterItem[];
  brands: TagFilterItem[];
  linkedProducts: TagFilterItem[];
  stock: string;
  blacklist: string;
  brand: string;
  category: string;
}

export const ScrapedProductTable = ({
  items,
  page,
  totalProducts,
  changePage,
  isLoading,
  searchTerm,
  handleSearchTermChange,
  supplierId,
  handleSupplierIdChange,
  limit,
  handleLimitChange,
  brandFilter,
  handleBrandFilterChange,
  categoryFilter,
  handleCategoryFilterChange,
  stockFilter,
  handleStockFilterChange,
  blacklistFilter,
  handleBlacklistFilterChange,
  linkedProductStatus,
  handleLinkedProductStatusChange,
  scrapedCategoryIds,
  handleScrapedCategoryIdsChange,
  scrapedCategoryInitialItems,
  scrapedBrandIds,
  handleScrapedBrandIdsChange,
  scrapedBrandInitialItems,
  linkedProductIds,
  handleLinkedProductIdsChange,
  linkedProductInitialItems,
  selectedProductIds,
  onSelectionChange,
  onBulkAssociate,
  onBulkDisassociate,
  selectedProductsMap,
}: ScrapedProductTableProps) => {
  const [selectedProduct, setSelectedProduct] = useState<ScrapedProduct>();
  const [productToAssociate, setProductToAssociate] = useState<
    ScrapedProduct | undefined
  >();
  const [productToDisassociate, setProductToDisassociate] = useState<
    ScrapedProduct | undefined
  >();
  const [selectedImage, setSelectedImage] = useState<{
    name: string;
    images: string[];
  } | null>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const { data: suppliers } = useGetAllSuppliers();
  const { mutate: updateScrapedProduct } = useUpdateScrapedProduct();

  const categoryRepository = useMemo(() => new ScrapedCategoryRepository(), []);
  const brandRepository = useMemo(() => new ScrapedBrandRepository(), []);
  const productRepository = useMemo(() => new ProductRepository(), []);

  const fetchCategories = useCallback(
    async (params: { name?: string; page: number; limit: number }) => {
      const result = await categoryRepository.findAll(params);

      return { data: result.data, totalPages: result.totalPages };
    },
    [categoryRepository],
  );

  const fetchBrands = useCallback(
    async (params: { name?: string; page: number; limit: number }) => {
      const result = await brandRepository.findAll(params);

      return { data: result.data, totalPages: result.totalPages };
    },
    [brandRepository],
  );

  const fetchProducts = useCallback(
    async (params: { name?: string; page: number; limit: number }) => {
      const result = await productRepository.findAll({
        searchTerm: params.name,
        page: params.page,
        limit: params.limit,
      });

      return {
        data: result.data.map((p) => ({ id: p.id, name: p.name })),
        totalPages: result.totalPages,
      };
    },
    [productRepository],
  );

  // What is applied now, as the draft shape (names come from the URL for ids picked earlier).
  const applied = useMemo<FilterDraft>(() => {
    const named = (
      ids: string[],
      known: { id: string; name: string }[] | undefined,
    ) => ids.map((id) => known?.find((k) => k.id === id) ?? { id, name: id });

    return {
      supplierId,
      categories: named(scrapedCategoryIds, scrapedCategoryInitialItems),
      brands: named(scrapedBrandIds, scrapedBrandInitialItems),
      linkedProducts: named(linkedProductIds, linkedProductInitialItems),
      stock: stockFilter,
      blacklist: blacklistFilter,
      brand: brandFilter,
      category: categoryFilter,
    };
  }, [
    supplierId,
    scrapedCategoryIds,
    scrapedCategoryInitialItems,
    scrapedBrandIds,
    scrapedBrandInitialItems,
    linkedProductIds,
    linkedProductInitialItems,
    stockFilter,
    blacklistFilter,
    brandFilter,
    categoryFilter,
  ]);

  const [draft, setDraft] = useState<FilterDraft>(applied);
  const patch = (changes: Partial<FilterDraft>) =>
    setDraft((prev) => ({ ...prev, ...changes }));

  const supplierName = (id: string) =>
    suppliers?.find((supplier) => supplier.id === id)?.name ?? id;

  const appliedTags = useMemo<AppliedFilter[]>(() => {
    const tags: AppliedFilter[] = [];

    if (applied.supplierId)
      tags.push({
        id: "supplier",
        label: "Supplier",
        value: supplierName(applied.supplierId),
      });
    if (applied.categories.length)
      tags.push({
        id: "categories",
        label: "Category",
        value: applied.categories.map((c) => c.name).join(", "),
      });
    if (applied.brands.length)
      tags.push({
        id: "brands",
        label: "Brand",
        value: applied.brands.map((b) => b.name).join(", "),
      });
    if (applied.linkedProducts.length)
      tags.push({
        id: "linkedProducts",
        label: "Linked product",
        value: applied.linkedProducts.map((p) => p.name).join(", "),
      });
    if (applied.stock)
      tags.push({
        id: "stock",
        label: "Stock",
        value: labelOf(STOCK_OPTIONS, applied.stock),
      });
    if (applied.blacklist)
      tags.push({
        id: "blacklist",
        label: "Blacklist",
        value: labelOf(BLACKLIST_OPTIONS, applied.blacklist),
      });
    if (applied.brand)
      tags.push({
        id: "brand",
        label: "Has brand",
        value: labelOf(HAS_BRAND_OPTIONS, applied.brand),
      });
    if (applied.category)
      tags.push({
        id: "category",
        label: "Has category",
        value: labelOf(HAS_CATEGORY_OPTIONS, applied.category),
      });

    return tags;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- supplier names load after the first render
  }, [applied, suppliers]);

  const applyDraft = (next: FilterDraft) => {
    if (next.supplierId !== supplierId) handleSupplierIdChange(next.supplierId);
    if (next.categories.map((c) => c.id).join() !== scrapedCategoryIds.join())
      handleScrapedCategoryIdsChange(
        next.categories.map((c) => c.id),
        next.categories,
      );
    if (next.brands.map((b) => b.id).join() !== scrapedBrandIds.join())
      handleScrapedBrandIdsChange(
        next.brands.map((b) => b.id),
        next.brands,
      );
    if (next.linkedProducts.map((p) => p.id).join() !== linkedProductIds.join())
      handleLinkedProductIdsChange(
        next.linkedProducts.map((p) => p.id),
        next.linkedProducts,
      );
    if (next.stock !== stockFilter) handleStockFilterChange(next.stock);
    if (next.blacklist !== blacklistFilter)
      handleBlacklistFilterChange(next.blacklist);
    if (next.brand !== brandFilter) handleBrandFilterChange(next.brand);
    if (next.category !== categoryFilter)
      handleCategoryFilterChange(next.category);
  };

  const EMPTY_DRAFT: FilterDraft = {
    supplierId: "",
    categories: [],
    brands: [],
    linkedProducts: [],
    stock: "",
    blacklist: "",
    brand: "",
    category: "",
  };

  const removeApplied = (id: string) => {
    const next = { ...applied };

    if (id === "supplier") next.supplierId = "";
    if (id === "categories") next.categories = [];
    if (id === "brands") next.brands = [];
    if (id === "linkedProducts") next.linkedProducts = [];
    if (id === "stock") next.stock = "";
    if (id === "blacklist") next.blacklist = "";
    if (id === "brand") next.brand = "";
    if (id === "category") next.category = "";
    applyDraft(next);
  };

  const clearAll = () => {
    handleSearchTermChange("");
    applyDraft(EMPTY_DRAFT);
    handleLinkedProductStatusChange("");
  };

  const selectedProductsStats = useMemo(() => {
    let linked = 0;
    let unlinked = 0;

    selectedProductsMap?.forEach((product) => {
      if (product.product) {
        if (!isPrimaryLink(product)) linked++;
      } else {
        unlinked++;
      }
    });

    return { linked, unlinked };
  }, [selectedProductsMap]);

  const handleUpdateBlacklist = useCallback(
    (product: ScrapedProduct, newValue: boolean) => {
      updateScrapedProduct(
        { id: product.id, blacklist: newValue },
        {
          onError: (error: any) => {
            notify.error(
              `Failed to update product ${product.name}`,
              error.response?.data?.message ||
                "Failed to update blacklist status. Please try again.",
            );
          },
        },
      );
    },
    [updateScrapedProduct],
  );

  const columns = useMemo<DataGridColumn<ScrapedProduct>[]>(
    () => [
      {
        id: "product",
        header: "Product",
        isRowHeader: true,
        minWidth: 260,
        cell: (product) => (
          <ProductCell
            product={product}
            onImageClick={() =>
              setSelectedImage({
                name: product.name,
                images: product.images || [],
              })
            }
          />
        ),
      },
      {
        id: "price",
        header: "Price",
        align: "end",
        width: 120,
        cell: (product) => <PriceCell product={product} />,
      },
      {
        id: "source",
        header: "Category / Brand",
        width: 190,
        cell: (product) => <SourceCell product={product} />,
      },
      {
        id: "link",
        header: "Link",
        width: 210,
        cell: (product) => (
          <LinkCell
            product={product}
            onFilterByLinkedProduct={() =>
              handleLinkedProductIdsChange(
                [product.product!.id],
                [{ id: product.product!.id, name: product.product!.name }],
              )
            }
          />
        ),
      },
      {
        id: "activity",
        header: "Last seen",
        width: 170,
        cell: (product) => <ActivityCell product={product} />,
      },
      {
        id: "whitelisted",
        header: "Whitelisted",
        width: 110,
        cell: (product) => (
          <WhitelistCell
            product={product}
            onChange={(blacklist) => handleUpdateBlacklist(product, blacklist)}
          />
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        width: 52,
        align: "end",
        cell: (product) => {
          const related = blacklistedRelatives(product);

          return (
            <RowActions
              product={product}
              importBlocked={
                product.blacklist ||
                Boolean(product.product) ||
                related.length > 0
              }
              onAssociate={() => setProductToAssociate(product)}
              onDisassociate={() => setProductToDisassociate(product)}
              onImport={() => setSelectedProduct(product)}
            />
          );
        },
      },
    ],
    [handleLinkedProductIdsChange, handleUpdateBlacklist],
  );

  const keys = useMemo(
    () => new Set<string | number>(selectedProductIds ?? []),
    [selectedProductIds],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <Segment
          aria-label="Link status"
          selectedKey={linkedProductStatus || "all"}
          onSelectionChange={(key) =>
            handleLinkedProductStatusChange(key === "all" ? "" : String(key))
          }
        >
          {LINK_TABS.map((tab, index) => (
            <Segment.Item key={tab.id} id={tab.id}>
              {index > 0 ? <Segment.Separator /> : null}
              {tab.label}
            </Segment.Item>
          ))}
        </Segment>

        <div className="flex min-w-0 max-w-full items-center gap-2">
          <SearchBox
            className="w-72"
            placeholder="Search products"
            value={searchTerm}
            onChange={handleSearchTermChange}
          />
          <FiltersPopover
            activeCount={appliedTags.length}
            isOpen={isFiltersOpen}
            onApply={() => {
              applyDraft(draft);
              setIsFiltersOpen(false);
            }}
            onClearAll={() => setDraft(EMPTY_DRAFT)}
            onOpenChange={(open) => {
              if (open) setDraft(applied);
              setIsFiltersOpen(open);
            }}
          >
            <FilterSelect
              allLabel="All"
              label="Supplier"
              options={(suppliers ?? []).map((s) => ({
                id: s.id,
                label: s.name,
              }))}
              value={draft.supplierId || null}
              onChange={(value) => patch({ supplierId: value ?? "" })}
            />
            <AsyncTagFilter
              fetchItems={fetchCategories}
              label="Category"
              placeholder="Search scraped categories"
              selected={draft.categories}
              onChange={(categories) => patch({ categories })}
            />
            <AsyncTagFilter
              fetchItems={fetchBrands}
              label="Brand"
              placeholder="Search scraped brands"
              selected={draft.brands}
              onChange={(brands) => patch({ brands })}
            />
            <AsyncTagFilter
              fetchItems={fetchProducts}
              label="Linked product"
              placeholder="Search catalog products"
              selected={draft.linkedProducts}
              onChange={(linkedProducts) => patch({ linkedProducts })}
            />
            <FilterSegment
              label="Stock"
              options={STOCK_OPTIONS}
              value={draft.stock}
              onChange={(stock) => patch({ stock })}
            />
            <FilterSegment
              label="Blacklist"
              options={BLACKLIST_OPTIONS}
              value={draft.blacklist}
              onChange={(blacklist) => patch({ blacklist })}
            />
            <FilterSegment
              label="Has brand"
              options={HAS_BRAND_OPTIONS}
              value={draft.brand}
              onChange={(brand) => patch({ brand })}
            />
            <FilterSegment
              label="Has category"
              options={HAS_CATEGORY_OPTIONS}
              value={draft.category}
              onChange={(category) => patch({ category })}
            />
          </FiltersPopover>
        </div>
      </div>

      {appliedTags.length > 0 ? (
        <div className="mb-4 shrink-0">
          <AppliedFilters
            filters={appliedTags}
            onClearAll={clearAll}
            onRemove={removeApplied}
          />
        </div>
      ) : null}

      <DataTable
        compact
        columns={columns}
        empty={{
          title: "No scraped products found",
          description: "Try adjusting your search or filters.",
          action:
            searchTerm || appliedTags.length || linkedProductStatus ? (
              <Button size="sm" variant="secondary" onPress={clearAll}>
                Clear all filters
              </Button>
            ) : undefined,
        }}
        getRowId={(product) => product.id}
        isLoading={isLoading && items.length === 0}
        label="Scraped products"
        minTableWidth={1040}
        pagination={{
          page,
          pageSize: limit,
          total: totalProducts,
          onPageChange: changePage,
          onPageSizeChange: handleLimitChange,
          itemLabel: "products",
        }}
        rows={items}
        selection={
          onSelectionChange
            ? {
                keys,
                allKeys: items.map((item) => item.id),
                onChange: (next) => onSelectionChange(next, items),
              }
            : undefined
        }
      />

      <BulkBar
        count={selectedProductIds?.size ?? 0}
        onClear={() => onSelectionChange?.(new Set(), items)}
      >
        {selectedProductsStats.unlinked > 0 && onBulkAssociate ? (
          <Button size="sm" variant="ghost" onPress={onBulkAssociate}>
            Link to product
          </Button>
        ) : null}
        {selectedProductsStats.linked > 0 && onBulkDisassociate ? (
          <Button size="sm" variant="danger-soft" onPress={onBulkDisassociate}>
            Unlink
          </Button>
        ) : null}
      </BulkBar>

      {selectedImage && (
        <ImageGalleryModal
          images={selectedImage.images}
          isOpen={!!selectedImage}
          name={selectedImage.name}
          onClose={() => setSelectedImage(null)}
        />
      )}

      {selectedProduct && (
        <ImportScrapedProductModal
          isOpen={Boolean(selectedProduct)}
          product={selectedProduct!}
          onClose={() => setSelectedProduct(undefined)}
        />
      )}

      {productToAssociate && (
        <AssociateProductModal
          isOpen={Boolean(productToAssociate)}
          scrapedProducts={[productToAssociate]}
          onClose={() => setProductToAssociate(undefined)}
        />
      )}

      {productToDisassociate && (
        <DisassociateProductModal
          isOpen={Boolean(productToDisassociate)}
          scrapedProducts={[productToDisassociate]}
          onClose={() => setProductToDisassociate(undefined)}
        />
      )}
    </div>
  );
};
